import './PlanEditPage.css';

import React, { useContext, useState } from 'react';
import Banner from '../../b2b-common/Banner';
import EditText from '../../b2b-common/EditText';
import { generateSelectField, handleObjectFieldChange } from '../../b2b-common/helpers';
import BooleanIconToggle from '../../b2b-common/BooleanIconToggle';
import { Edit, EditOff } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import Frame from '../../b2b-common/Frame';
import { useLocation, useNavigate } from 'react-router-dom';
import { deletePlanImage, saveNewPlan, updatePlan, uploadPlanImage } from '../../../services/b2b-plans-apiService';
import { LoginContext } from '../../../App';
import { B2BContentContext } from '../../layout/Layout';
import { showNotification } from '../../b2b-common/Notification';
import CustomButton from '../../b2b-common/CustomButton';
import useB2BTranslation from '../../b2b-common/hooks/useB2BTranslation';
import RichNoteEditor from '../../b2b-logbook/components/notes/RichNoteEditor';
import B2BImageCard from '../../b2b-common/B2BImageCard';

const PlanEditPage = () => {

    const { t } = useB2BTranslation('b2b-plans');
    const { setNotification } = React.useContext(B2BContentContext);
    const navigate = useNavigate();
    const location = useLocation();
    const { plan } = location.state || {};
    const plansExplorerUrl = '/plans/plans-explorer';
    const from = location.state?.from || plansExplorerUrl;

    const { loginData } = useContext(LoginContext);
    const [error, setError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [planImage, setPlanImage] = useState(null);
    const [removePlanImage, setRemovePlanImage] = useState(false);

    const initialPlan = plan ? {
        ...plan,
        planStartDate: dayjs(plan.planStartDate).startOf('day'),
        planEndDate: dayjs(plan.planEndDate).startOf('day'),
    } : {
        planName: t('New plan'),
        planDescription: t('Plan description'),
        readOnly: false,
        planStatus: 'IDEA',
        planStartDate: dayjs().startOf('day'),
        planEndDate: dayjs().add(1, 'month').startOf('day'),
        imagePath: null,
    };

    //console.log('initialPlan:', initialPlan);

    const [currentPlan, setCurrentPlan] = useState(initialPlan);

    //console.log('currentPlan:', currentPlan);

    const handleSave = async () => {
        const planToSave = {
            ...currentPlan,
            planStartDate: currentPlan.planStartDate.format('YYYY-MM-DD'),
            planEndDate: currentPlan.planEndDate.format('YYYY-MM-DD'),
        };

        if (plan && plan.planUUID) { // Update plan
            //console.log('update plan');
            try {
                setIsSaving(true);
                const updatedPlan = await updatePlan(loginData.accessToken, planToSave);
                //console.log('updatedPlan:', updatedPlan);
                if (planImage && !removePlanImage) {
                    const uploadImageResponse = await uploadPlanImage(loginData.accessToken, updatedPlan.planUUID, planImage);
                    //console.log('uploadImageResponse:', uploadImageResponse);
                } else if (removePlanImage) {
                    //console.log('delete image');
                    const deleteImageResponse = await deletePlanImage(loginData.accessToken, updatedPlan.planUUID);
                    //console.log('deleteImageResponse:', deleteImageResponse);
                }
                showNotification(setNotification, t('Plan updated'), 'success', 3000);
                if (from === plansExplorerUrl) {
                    const updatedFrom = `${from}?selectedPlanId=${encodeURIComponent(updatedPlan.planId)}`;
                    navigate(updatedFrom);
                } else {
                    navigate(from);
                }
            } catch (error) {
                setError(error.message);
                showNotification(setNotification, error.message, 'error', 3000);
            } finally {
                setIsSaving(false);
            }
        } else { // New plan
            //console.log('save plan');
            try {
                setIsSaving(true);
                const newPlan = await saveNewPlan(loginData.accessToken, planToSave);
                //console.log('newPlan:', newPlan);
                if (planImage && !removePlanImage) {
                    const uploadImageResponse = await uploadPlanImage(loginData.accessToken, newPlan.planUUID, planImage);
                    //console.log('uploadImageResponse:', uploadImageResponse);
                }
                showNotification(setNotification, t('Plan created'), 'success', 3000);
                navigate(from);
            } catch (error) {
                setError(error.message);
            } finally {
                setIsSaving(false);
            }
        }
    }

    const planStatusOptions = [
        { value: 'IDEA', label: t('Idea') },
        { value: 'PENDING_APPROVAL', label: t('Pending Approval') },
        { value: 'APPROVED', label: t('Approved') },
    ];

    return (
        <div className='plan-edit-page-container'>
            <Banner message={plan ? plan.planName : t('New plan')} underline={false}
                // sectionTittle={'Plan:'} 
            />
            <div className='plan-edit-page-fields'>
                <div className='plan-edit-page-fields-column-left'>
                    <Frame title={t('Plan details')} className='plan-edit-page-details-frame transparent-no-title-theme'>
                        <div className='plan-edit-page-details'>
                            <div className='plan-edit-page-details-row'>
                                <EditText object={currentPlan} objectField={'planName'} setObjectField={setCurrentPlan} label={t('Plan name')} />
                                {generateSelectField(planStatusOptions,
                                    {
                                        label: t('Plan status'),
                                        value: currentPlan.planStatus,
                                        onChange: (e) => handleObjectFieldChange('planStatus', e.target.value, setCurrentPlan),
                                        className: 'plan-edit-page-status-field',
                                    }
                                )}
                                <BooleanIconToggle
                                    value={currentPlan.readOnly}
                                    onChange={(value) => handleObjectFieldChange('readOnly', value, setCurrentPlan)}
                                    trueIcon={<EditOff />}
                                    trueText={t('Read only')}
                                    falseIcon={<Edit />}
                                    falseText={t('Edit mode')}
                                    className='plan-edit-page-read-only-toggle'
                                />
                            </div>
                            <div className='plan-edit-page-details-row'>
                                <RichNoteEditor
                                    value={currentPlan.planDescription}
                                    onChange={(content) => setCurrentPlan({ ...currentPlan, planDescription: content })}
                                    className={'plan-edit-page-description-rich-editor'}
                                />
                            </div>
                        </div>
                    </Frame>
                </div>
                <div className='plan-edit-page-fields-column-right'>
                    <Frame title='Plan dates' className='plan-edit-page-dates-frame transparent-no-title-theme'>
                        <div className='plan-edit-page-dates'>
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='es'>
                                <DatePicker
                                    label={t('Plan start date')}
                                    value={currentPlan.planStartDate}
                                    onChange={(date) => handleObjectFieldChange('planStartDate', date, setCurrentPlan)}
                                    slotProps={{ textField: { variant: 'standard' } }}
                                />
                            </LocalizationProvider>
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='es'>
                                <DatePicker
                                    label={t('Plan end date')}
                                    value={currentPlan.planEndDate}
                                    onChange={(date) => handleObjectFieldChange('planEndDate', date, setCurrentPlan)}
                                    slotProps={{ textField: { variant: 'standard' } }}
                                />
                            </LocalizationProvider>
                        </div>
                    </Frame>
                    <Frame title='Plan media' className='plan-edit-page-media-frame transparent-no-title-theme'>
                        <B2BImageCard imagePath={currentPlan.imagePath}
                            showChangeButton={true}
                            requestChangeConfirmation={true}
                            onChange={(file) => setPlanImage(file)}
                            showDeleteButton={true}
                            requestDeleteConfirmation={true}
                            onDelete={() => setRemovePlanImage(true)}
                            synchrousUpdate={false}
                            className='plan-edit-page-image-card'
                        />
                    </Frame>
                </div>
            </div>
            <div className='plan-edit-page-buttons'>
                <CustomButton
                    label={t('Save')}
                    onClick={handleSave}
                />
                <CustomButton
                    label={t('Cancel')}
                    onClick={() => navigate(from)}
                    className={'white_02'}
                />
            </div>
        </div>
    );
};

export default PlanEditPage;